import React from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

// Declare atlos for TypeScript
declare global {
  interface Window {
    atlos: {
      Pay: (params: {
        merchantId: string;
        orderId: string;
        orderAmount: number;
        orderCurrency?: string;
        orderDescription?: string;
        successUrl?: string;
        cancelUrl?: string;
        theme?: 'light' | 'dark';
        recurring?: boolean;
        recurringInterval?: 'monthly' | 'yearly';
      }) => void;
    };
  }
}

interface AtlosPayButtonProps {
  planType: 'monthly' | 'yearly' | 'lifetime';
  amount: number;
  className?: string;
  onPaymentStarted?: () => void;
  children: React.ReactNode;
}

const AtlosPayButton: React.FC<AtlosPayButtonProps> = ({
  planType,
  amount,
  className = '',
  onPaymentStarted,
  children
}) => {
  const handlePayment = () => {
    const atlos = (globalThis as typeof globalThis & { atlos?: typeof globalThis.atlos }).atlos;
    if (!atlos) {
      toast.error('Payment system not loaded yet. Please try again in a moment.');
      return;
    }

    const orderId = `typerush_${planType}_${Date.now()}`;
    const isRecurring = planType === 'monthly' || planType === 'yearly';
    const planLabels: Record<typeof planType, string> = { monthly: 'Monthly', yearly: 'Yearly', lifetime: 'Lifetime' };
    const orderDescription = `TypeRush Premium - ${planLabels[planType]} Plan`;
    const recurringInterval: 'monthly' | 'yearly' | undefined =
      planType === 'monthly' || planType === 'yearly' ? planType : undefined;
    const origin = globalThis.location?.origin ?? '';
    
    try {
      atlos.Pay({
        merchantId: process.env.REACT_APP_ATLOS_MERCHANT_ID || 'GT8LTIFOMN',
        orderId: orderId,
        orderAmount: amount,
        orderCurrency: 'USD',
        orderDescription,
        theme: 'dark',
        recurring: isRecurring,
        recurringInterval,
        successUrl: `${origin}/premium?success=true&plan=${planType}`,
        cancelUrl: `${origin}/premium?cancelled=true`
      });

      toast.success('Opening secure payment window...');
      
      if (onPaymentStarted) {
        onPaymentStarted();
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to open payment window. Please try again.');
    }
  };

  return (
    <motion.button
      onClick={handlePayment}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={className}
    >
      {children}
    </motion.button>
  );
};

export default AtlosPayButton;
